/* Telas por perfil e por pessoa (itens 8 e 9 da revisão da Amanda).
 *
 * A API fala em permissões granulares; quem atribui acesso pensa em telas. O servidor devolve a
 * tradução pronta — o catálogo de telas do lado, o que cada perfil contém e, para cada pessoa,
 * as telas ativas e as que foram ajustadas depois do perfil. Aqui só se lê e se grava isso. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { atualizarJson, pedirJson } from "./api";
import { CHAVE_DAS_TELAS, esquemaUsuario } from "./usuarios";

export type LadoDasTelas = "cliente" | "interno";

const esquemaTela = z.object({
  chave: z.string().min(1),
  rotulo: z.string().min(1),
  descricao: z.string(),
  principal: z.string().min(1),
  permissoes: z.array(z.string()),
  sensivel: z.boolean(),
  aviso_sensivel: z.string().nullable(),
  concedivel: z.boolean(),
});

const esquemaPerfilComTelas = z.object({
  chave: z.string().min(1),
  nome: z.string().min(1),
  personalizado: z.boolean(),
  descricao: z.string().nullable(),
  telas: z.array(z.string()),
  permissoes: z.array(z.string()).default([]),
});

const esquemaPessoaComTelas = z.object({
  usuario: esquemaUsuario,
  ativas: z.array(z.string()),
  ajustadas: z.array(z.string()),
  permissoes: z.array(z.string()).default([]),
  ajustes: z.array(z.object({ permissao_chave: z.string(), efeito: z.enum(["grant", "revoke"]) })).default([]),
});

const esquemaMatriz = z.object({
  lado: z.enum(["cliente", "interno"]),
  telas: z.array(esquemaTela),
  perfis: z.array(esquemaPerfilComTelas),
  pessoas: z.array(esquemaPessoaComTelas),
});

export type TelaDoPortal = z.infer<typeof esquemaTela>;
export type PerfilComTelas = z.infer<typeof esquemaPerfilComTelas>;
export type PessoaComTelas = z.infer<typeof esquemaPessoaComTelas>;
export type MatrizDeTelas = z.infer<typeof esquemaMatriz>;

export function useMatrizDeTelas(lado: LadoDasTelas) {
  return useQuery({
    queryKey: [...CHAVE_DAS_TELAS, lado],
    queryFn: ({ signal }) =>
      pedirJson(`/usuarios/telas?lado=${lado}&incluir_inativos=true`, esquemaMatriz, { signal }),
  });
}

export function useAjustarTelas() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, telas }: { usuarioId: string; telas: Record<string, boolean> }) =>
      atualizarJson(`/usuarios/${encodeURIComponent(usuarioId)}/telas`, esquemaPessoaComTelas, {
        telas,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_DAS_TELAS });
      void cliente.invalidateQueries({ queryKey: ["usuarios"] });
      void cliente.invalidateQueries({ queryKey: ["usuarios", "excecoes"] });
    },
  });
}

/** Rótulos das telas que um perfil contém, na ordem do catálogo. */
export function telasDoPerfil(matriz: MatrizDeTelas | undefined, chavePerfil: string) {
  const perfil = matriz?.perfis.find((item) => item.chave === chavePerfil);
  if (!matriz || !perfil) return null;
  const contem = matriz.telas.filter((tela) => perfil.telas.includes(tela.chave));
  const nao = matriz.telas.filter((tela) => !perfil.telas.includes(tela.chave));
  return { contem, nao };
}

/** "5 de 7" — o resumo de quantas telas a pessoa enxerga. */
export function resumoDeTelas(ativas: number, total: number): string {
  return ativas === total ? "todas" : `${ativas} de ${total}`;
}

/** "3 de 7" para a pessoa, ou "—" quando a matriz não a traz. */
export function resumoDaPessoa(matriz: MatrizDeTelas, usuarioId: string): string {
  const pessoa = matriz.pessoas.find((item) => item.usuario.id === usuarioId);
  return pessoa ? resumoDeTelas(pessoa.ativas.length, matriz.telas.length) : "—";
}
